import React, { useEffect, useId, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw, Save } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import LoadFailure from "../../components/ui/LoadFailure";
import { useT } from "../../lib/LanguageContext";
import { lookup } from "../../lib/i18n";
import { HQ_SITE } from "../../constants/testIds/hqSite";
import siteApi from "./siteApi";
import useSiteContentAdmin from "./useSiteContentAdmin";
import usePlatformMedia from "./usePlatformMedia";
import MediaPickerField from "./MediaPickerField";
import { siteErrorMessage } from "./siteErrors";

/**
 * Content tab: every editable field of the landing page, grouped by the schema's sections
 * in render order (contract §5). A text field holds one override per locale; an empty
 * locale means "no override" and that locale renders its dictionary default — shown here
 * as the input's placeholder, read from the `saas` dictionary for THAT locale, since a
 * French override is compared by the operator against the French default, not the
 * current UI language's.
 *
 * The client-side checks (length, undeclared `{placeholders}`) mirror the server's 422s so
 * a refusal is visible before the round trip; the server stays the authority and its
 * refusals are shown on the field too.
 */

// The server's own placeholder grammar (modules/site_content/service.py `_PLACEHOLDER`).
const PLACEHOLDER = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g;

/** The distinct `{names}` a value uses. */
const placeholdersIn = (value) => [...new Set([...value.matchAll(PLACEHOLDER)].map((m) => m[1]))];

const valueKey = (locale) => `value_${locale}`;

function draftOf(block, locales) {
  return Object.fromEntries(locales.map((l) => [valueKey(l), block?.[valueKey(l)] || ""]));
}

/** The client-side refusals of a text draft, one message per offending locale. */
function draftProblems(draft, field, { locales, max, t, localeLabel }) {
  const problems = [];
  for (const locale of locales) {
    const value = draft[valueKey(locale)];
    if (value.length > max) {
      problems.push(t("hsite.content.tooLong", { locale: localeLabel(locale), max }));
    }
    const undeclared = placeholdersIn(value).filter((name) => !field.vars.includes(name));
    if (undeclared.length > 0) {
      problems.push(t("hsite.content.undeclared", {
        locale: localeLabel(locale),
        placeholders: undeclared.map((n) => `{${n}}`).join(", "),
      }));
    }
  }
  return problems;
}

function TextFieldEditor({ field, block, schema, content, t, localeLabel, readOnly }) {
  const locales = schema.locales;
  const max = schema.limits[field.kind];
  const [draft, setDraft] = useState(() => draftOf(block, locales));
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");
  const errorId = useId();

  // A save or reset elsewhere replaces the stored block: the draft follows it.
  useEffect(() => { setDraft(draftOf(block, locales)); }, [block, locales]);

  const stored = draftOf(block, locales);
  const dirty = locales.some((l) => draft[valueKey(l)] !== stored[valueKey(l)]);
  const problems = draftProblems(draft, field, { locales, max, t, localeLabel });
  const Control = field.kind === "multiline" ? Textarea : Input;

  const save = async () => {
    setBusy(true);
    setServerError("");
    try {
      await content.saveText(field.key, draft);
      toast.success(t("hsite.content.saved"));
    } catch (e) {
      setServerError(siteErrorMessage(e, t));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    setServerError("");
    try {
      await content.reset(field.key);
      toast.success(t("hsite.content.resetDone"));
    } catch (e) {
      setServerError(siteErrorMessage(e, t));
    } finally {
      setBusy(false);
    }
  };

  const messages = serverError ? [...problems, serverError] : problems;

  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid={HQ_SITE.field(field.key)}>
      <FieldHeader field={field} overridden={Boolean(block)} t={t} />
      <div className="grid md:grid-cols-3 gap-2">
        {locales.map((locale) => {
          const inputId = `${errorId}-${locale}`;
          return (
            <div key={locale}>
              <label htmlFor={inputId} className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">
                {localeLabel(locale)}
              </label>
              <Control
                id={inputId}
                value={draft[valueKey(locale)]}
                onChange={(e) => {
                  const next = e.target.value;
                  setDraft((prev) => ({ ...prev, [valueKey(locale)]: next }));
                  setServerError("");
                }}
                placeholder={lookup("saas", field.key, locale)}
                readOnly={readOnly}
                rows={field.kind === "multiline" ? 4 : undefined}
                aria-describedby={messages.length > 0 ? errorId : undefined}
                aria-invalid={messages.length > 0 || undefined}
                className="bg-white border-slate-200 text-sm"
                data-testid={HQ_SITE.fieldInput(field.key, locale)}
              />
            </div>
          );
        })}
      </div>
      {messages.length > 0 && (
        <ul id={errorId} role="alert" className="mt-2 space-y-0.5 text-xs text-rose-600" data-testid={HQ_SITE.fieldError(field.key)}>
          {messages.map((m) => <li key={m}>{m}</li>)}
        </ul>
      )}
      {!readOnly && (
      <div className="mt-3 flex items-center gap-2 justify-end">
        {block && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={reset}
            disabled={busy}
            className="h-8 text-xs rounded-xl"
            data-testid={HQ_SITE.fieldReset(field.key)}
          >
            <RotateCcw className="w-3 h-3 mr-1" /> {t("hsite.content.reset")}
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          onClick={save}
          disabled={busy || !dirty || problems.length > 0}
          className="bg-sol-gold text-black hover:bg-sol-gold/90 h-8 text-xs rounded-xl"
          data-testid={HQ_SITE.fieldSave(field.key)}
        >
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
          {t("hsite.content.save")}
        </Button>
      </div>
      )}
    </div>
  );
}

function ImageFieldEditor({ field, block, mediaById, content, t, api, readOnly }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // What the operator just picked: the picker may have uploaded it, in which case this
  // tab's media list does not hold it yet.
  const [picked, setPicked] = useState(null);
  const current = !block?.media_id
    ? null
    : mediaById[block.media_id] || (picked?.media_id === block.media_id ? picked : null);

  const run = async (write, success) => {
    setBusy(true);
    setError("");
    try {
      await write();
      toast.success(success);
    } catch (e) {
      setError(siteErrorMessage(e, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid={HQ_SITE.field(field.key)}>
      <FieldHeader field={field} overridden={Boolean(block)} t={t} />
      <MediaPickerField
        label={block ? t("hsite.content.imageChosen") : t("hsite.content.imageDefault")}
        value={current ? { media_id: current.media_id, url: current.url } : null}
        onChange={(choice) => {
          setPicked(choice);
          run(() => content.saveImage(field.key, choice.media_id), t("hsite.content.saved"));
        }}
        onClear={() => run(() => content.reset(field.key), t("hsite.content.resetDone"))}
        disabled={busy}
        readOnly={readOnly}
        testId={HQ_SITE.fieldImage(field.key)}
        api={api}
      />
      {error && (
        <p role="alert" className="mt-2 text-xs text-rose-600" data-testid={HQ_SITE.fieldError(field.key)}>{error}</p>
      )}
    </div>
  );
}

function FieldHeader({ field, overridden, t }) {
  return (
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="min-w-0">
        <code className="text-xs text-amber-700 break-all">{field.key}</code>
        {field.vars.length > 0 && (
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <span className="text-[11px] text-slate-400">{t("hsite.content.vars")}</span>
            {field.vars.map((name) => (
              <span
                key={name}
                className="text-[11px] font-mono px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-600"
                data-testid={HQ_SITE.fieldVar(field.key, name)}
              >
                {`{${name}}`}
              </span>
            ))}
          </div>
        )}
      </div>
      <span
        className={`flex-shrink-0 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${overridden ? "bg-sol-gold/15 text-amber-700" : "bg-slate-100 text-slate-400"}`}
        data-testid={HQ_SITE.fieldOverridden(field.key)}
      >
        {overridden ? t("hsite.content.overridden") : t("hsite.content.default")}
      </span>
    </div>
  );
}

// `readOnly`: the operator may not write (settings.write); `canReadMedia`: the operator holds
// data.media.read, without which the library (the chosen images' previews) is not asked for.
export default function ContentTab({ schema, api = siteApi, readOnly = false, canReadMedia = true }) {
  const t = useT("superadmin");
  const content = useSiteContentAdmin({ api });
  const media = usePlatformMedia({ api, enabled: canReadMedia });
  const [sectionId, setSectionId] = useState(schema.sections[0]?.id ?? null);

  const sectionLabels = {
    navbar: t("hsite.section.navbar"),
    hero: t("hsite.section.hero"),
    dualpillar: t("hsite.section.dualpillar"),
    ecosystem: t("hsite.section.ecosystem"),
    pricing: t("hsite.section.pricing"),
    turnkey: t("hsite.section.turnkey"),
    boosters: t("hsite.section.boosters"),
    faq: t("hsite.section.faq"),
    finalCta: t("hsite.section.finalCta"),
    footer: t("hsite.section.footer"),
  };
  const localeLabels = { fr: t("hsite.locale.fr"), en: t("hsite.locale.en"), es: t("hsite.locale.es") };
  const localeLabel = (locale) => localeLabels[locale] || locale.toUpperCase();

  const mediaById = useMemo(
    () => Object.fromEntries(media.items.map((m) => [m.media_id, m])),
    [media.items],
  );

  if (content.status === "loading") return <Loader2 className="w-6 h-6 animate-spin text-amber-600" />;
  if (content.status === "error") {
    return (
      <LoadFailure
        message={siteErrorMessage(content.error, t)}
        onRetry={content.reload}
        retryLabel={t("hsite.retry")}
        testId={HQ_SITE.loadError}
        retryTestId={HQ_SITE.loadRetry}
      />
    );
  }

  const section = schema.sections.find((s) => s.id === sectionId);

  return (
    <div>
      <p className="text-sm text-slate-500 max-w-3xl mb-4">{t("hsite.content.hint")}</p>
      <div className="flex flex-wrap gap-2 mb-5" role="tablist" aria-label={t("hsite.content.sections")}>
        {schema.sections.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={s.id === sectionId}
            onClick={() => setSectionId(s.id)}
            data-testid={HQ_SITE.sectionNav(s.id)}
            className={`px-3 py-1.5 rounded-xl text-xs ${s.id === sectionId ? "bg-sol-gold/15 text-amber-700 border border-sol-gold/30" : "bg-white text-slate-500 border border-slate-200"}`}
          >
            {sectionLabels[s.id] || s.id}
          </button>
        ))}
      </div>
      {section && (
        <div role="tabpanel" className="space-y-2" data-testid={HQ_SITE.sectionPanel(section.id)}>
          {section.fields.map((field) => (field.kind === "image" ? (
            <ImageFieldEditor
              key={field.key}
              field={field}
              block={content.blocks[field.key]}
              mediaById={mediaById}
              content={content}
              t={t}
              api={api}
              readOnly={readOnly}
            />
          ) : (
            <TextFieldEditor
              key={field.key}
              field={field}
              block={content.blocks[field.key]}
              schema={schema}
              content={content}
              t={t}
              localeLabel={localeLabel}
              readOnly={readOnly}
            />
          )))}
        </div>
      )}
    </div>
  );
}
